import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@devhop/ui";
import {
  RiArrowLeftLine,
  RiArrowRightLine,
  RiBookOpenLine,
  RiRotateLockLine,
} from "@remixicon/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useFeedProductMonitorQuery } from "@/modules/cf-feed/presentation/api/queries";
import type { FeedProductMonitorProduct } from "@/modules/cf-feed/presentation/api/types";
import type { WorkspaceSnapshot } from "@/modules/cf-live/domain/contracts/live-workspace";
import {
  useWorkspaceEnabled,
  useWorkspacePollInterval,
  workspaceRequest,
} from "@/modules/cf-live/presentation/api/workspace-client";
import { AppImage } from "@/shared/ui/AppImage";
import { QueryState } from "@/shared/ui/QueryState";
import {
  getMonitorDisplayRotationShellStyle,
  isMonitorDisplaySideways,
  type MonitorDisplayRotation,
  nextMonitorDisplayRotation,
  parseMonitorDisplayRotation,
  readStoredMonitorDisplayRotation,
  writeStoredMonitorDisplayRotation,
} from "../lib/feed-product-monitor-display-rotation";
import {
  getMonitorQueueNeighbors,
  useFeedProductMonitorDisplaySelection,
} from "../lib/feed-product-monitor-display-selection";
import { useFeedProductMonitorVisualHighlight } from "../lib/feed-product-monitor-visual-highlight";
import {
  FEED_COMMENT_ORDER_FORMAT_ROWS,
  FeedCommentOrderFormatTable,
} from "../ui/FeedCommentOrderGuide";
import { FeedProductMonitorTable } from "../ui/FeedProductMonitorTable";

function resolveInitialRotation(
  searchRotate: string | undefined,
): MonitorDisplayRotation {
  if (searchRotate != null) {
    return parseMonitorDisplayRotation(searchRotate);
  }
  return readStoredMonitorDisplayRotation();
}

/**
 * คงหน้าจอเดิมไว้ โดยงานที่เปิด workspace ใช้สินค้าปัจจุบันชุดเดียวกับ OBS
 * localStorage ใช้เฉพาะงานเดิมเมื่อปิด feature flag
 */
export function FeedProductMonitorDisplayPage() {
  const { id: feedId } = useParams({ from: "/app/feeds/$id/monitor-display" });
  const search = useSearch({ from: "/app/feeds/$id/monitor-display" });
  const navigate = useNavigate({ from: "/app/feeds/$id/monitor-display" });
  const [rotation, setRotation] = useState<MonitorDisplayRotation>(() =>
    resolveInitialRotation(search.rotate),
  );
  const [guideOpen, setGuideOpen] = useState(false);

  const legacySelection = useFeedProductMonitorDisplaySelection(feedId);
  const capability = useWorkspaceEnabled();
  const sharedDisplay = capability.data?.enabled === true;
  const resolved = useQuery({
    queryKey: ["workspace-resolve", "feed", feedId],
    queryFn: () =>
      workspaceRequest<{ id: string }>(
        `/resolve?kind=feed&id=${encodeURIComponent(feedId)}`,
      ),
    enabled: sharedDisplay,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const workspaceId = resolved.data?.id;
  const pollInterval = useWorkspacePollInterval();
  const workspace = useQuery({
    queryKey: ["workspace", workspaceId, "monitor"],
    queryFn: ({ signal }) =>
      workspaceRequest<WorkspaceSnapshot>(
        `/${workspaceId}?view=monitor`,
        undefined,
        signal,
      ),
    enabled: sharedDisplay && Boolean(workspaceId),
    refetchInterval: pollInterval,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const activeProductId = workspace.data?.state.activeProductId;
  const neighbors = useMemo(
    () =>
      getMonitorQueueNeighbors(
        workspace.data?.queue ?? [],
        activeProductId,
        workspace.data?.state.excludedProductIds ?? [],
      ),
    [
      workspace.data?.queue,
      activeProductId,
      workspace.data?.state.excludedProductIds,
    ],
  );
  const displayProductIds = useMemo(
    () =>
      sharedDisplay
        ? activeProductId
          ? [activeProductId]
          : []
        : legacySelection.displayProductIds,
    [sharedDisplay, activeProductId, legacySelection.displayProductIds],
  );
  const monitorProductIds = useMemo(
    () =>
      sharedDisplay && activeProductId
        ? [activeProductId, neighbors.previous?.id, neighbors.next?.id].filter(
            (id): id is string => Boolean(id),
          )
        : displayProductIds,
    [
      sharedDisplay,
      activeProductId,
      neighbors.previous?.id,
      neighbors.next?.id,
      displayProductIds,
    ],
  );
  const monitorQuery = useFeedProductMonitorQuery(
    feedId,
    !capability.isError &&
      (capability.data?.enabled === false ||
        (sharedDisplay && Boolean(workspace.data))),
    monitorProductIds,
    sharedDisplay ? 1500 : undefined,
  );
  const { isProductHighlighted, isVariantHighlighted } =
    useFeedProductMonitorVisualHighlight(feedId);

  const products = useMemo(
    () => monitorQuery.data?.pages.flatMap((page) => page.products) ?? [],
    [monitorQuery.data],
  );
  const displayProducts = useMemo(
    () =>
      displayProductIds.flatMap((id) =>
        products.filter((product) => product.productId === id),
      ),
    [displayProductIds, products],
  );
  const isLoading =
    capability.isLoading ||
    (sharedDisplay && (resolved.isLoading || workspace.isLoading)) ||
    monitorQuery.isLoading;
  const error =
    capability.error ??
    (sharedDisplay ? (resolved.error ?? workspace.error) : null) ??
    monitorQuery.error;
  const totals = monitorQuery.data?.pages[0]?.totals ?? null;
  const compactDisplayLayout = isMonitorDisplaySideways(rotation);
  const fitVariants = sharedDisplay && displayProducts.length === 1;

  const cycleRotation = () => {
    const next = nextMonitorDisplayRotation(rotation);
    setRotation(next);
    writeStoredMonitorDisplayRotation(next);
    void navigate({
      search: (prev) => ({ ...prev, rotate: String(next) }),
      replace: true,
    });
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-background">
      <div
        className={`@container/monitor min-w-0 bg-background p-3 sm:p-4 ${fitVariants ? "flex flex-col" : ""}`}
        style={getMonitorDisplayRotationShellStyle(rotation)}
      >
        <div className="sticky top-0 z-50 mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border bg-background/95 p-2 shadow-sm backdrop-blur-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <RiBookOpenLine className="size-4 shrink-0 text-primary" />
            <p className="truncate font-mono text-muted-foreground text-xs sm:text-sm">
              {FEED_COMMENT_ORDER_FORMAT_ROWS.map((row) => row.example).join(
                "  ·  ",
              )}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setGuideOpen(true)}
            >
              <RiBookOpenLine className="size-4" />
              ວິທີພິມ CF
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={cycleRotation}
            >
              <RiRotateLockLine className="size-4" />
              ຫມຸນ {rotation}°
            </Button>
          </div>
        </div>

        <QueryState
          isLoading={isLoading}
          isError={Boolean(error)}
          error={error}
          onRetry={() => {
            if (capability.isError) void capability.refetch();
            else if (sharedDisplay && resolved.isError) void resolved.refetch();
            else if (sharedDisplay && workspace.isError)
              void workspace.refetch();
            else void monitorQuery.refetch();
          }}
          isFetching={
            monitorQuery.isFetching && !monitorQuery.isFetchingNextPage
          }
          variant="fullscreen"
          className={fitVariants ? "flex min-h-0 flex-1 flex-col" : undefined}
        >
          <FeedProductMonitorTable
            products={displayProducts}
            totals={totals}
            isLoading={monitorQuery.isLoading}
            defaultExpandProducts
            flashOnValueChange
            compactDisplayLayout={compactDisplayLayout}
            compactVariants={fitVariants}
            queueSides={
              sharedDisplay && activeProductId
                ? {
                    previous: (
                      <MonitorQueueCard
                        direction="previous"
                        neighbor={neighbors.previous}
                        product={products.find(
                          (p) => p.productId === neighbors.previous?.id,
                        )}
                      />
                    ),
                    next: (
                      <MonitorQueueCard
                        direction="next"
                        neighbor={neighbors.next}
                        product={products.find(
                          (p) => p.productId === neighbors.next?.id,
                        )}
                      />
                    ),
                  }
                : undefined
            }
            isProductOnDisplay={(id) => displayProductIds.includes(id)}
            getProductDisplayPosition={(id) => {
              const index = sharedDisplay
                ? (workspace.data?.queue.findIndex((item) => item.id === id) ??
                  -1)
                : displayProductIds.indexOf(id);
              return index < 0 ? null : index + 1;
            }}
            isProductHighlighted={
              sharedDisplay ? undefined : isProductHighlighted
            }
            isVariantHighlighted={
              sharedDisplay ? undefined : isVariantHighlighted
            }
            emptyMessage={
              sharedDisplay
                ? "ຈໍຖືກເຊື່ອງ — ໄປທີ່ ຈັດການສິນຄ້າ / OBS ແລ້ວກົດ ສະແດງດຽວນີ້"
                : "ຍັງບໍ່ໄດ້ເລືອກສິນຄ້າສຳລັບໜ້າຈໍ — ເລືອກຈາກແທັບ ຕິດຕາມສິນຄ້າ"
            }
          />
        </QueryState>
      </div>

      <Sheet open={guideOpen} onOpenChange={setGuideOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
        >
          <SheetHeader className="border-b px-4 pb-4 text-left">
            <SheetTitle className="flex items-center gap-2">
              <RiBookOpenLine className="size-5 text-primary" />
              ວິທີພິມຄອມເມັນ CF
            </SheetTitle>
            <SheetDescription>ໃຊ້ຮູບແບບເຫຼົ່ານີ້ເວລາຮັບຄຳສັ່ງຊື້ໃນ Live</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 px-4 py-5">
            <h2 className="font-medium">ຮູບແບບທີ່ໃຊ້ໄດ້</h2>
            <FeedCommentOrderFormatTable />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MonitorQueueCard({
  direction,
  neighbor,
  product,
}: {
  direction: "previous" | "next";
  neighbor: ReturnType<typeof getMonitorQueueNeighbors>["previous"];
  product: FeedProductMonitorProduct | undefined;
}) {
  const label = direction === "previous" ? "ຄິວກ່ອນໜ້າ" : "ຄິວຖັດໄປ";
  const Arrow = direction === "previous" ? RiArrowLeftLine : RiArrowRightLine;
  return (
    <Card
      aria-labelledby={`monitor-queue-${direction}`}
      className="@container/queue-neighbor min-w-0 gap-3 py-3 shadow-none"
    >
      <CardHeader className="gap-3 px-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2
            id={`monitor-queue-${direction}`}
            className="flex items-center gap-1.5 font-semibold @[18rem]/queue-neighbor:text-base text-sm"
          >
            <Arrow className="size-4 shrink-0" aria-hidden />
            {label}
          </h2>
          {neighbor && (
            <Badge variant="secondary">ລຳດັບ {neighbor.position}</Badge>
          )}
        </div>
        {neighbor ? (
          <div className="flex min-w-0 @[18rem]/queue-neighbor:flex-row flex-col items-start gap-2">
            <AppImage
              key={neighbor.id}
              src={product?.imageUrl}
              alt=""
              fit="contain"
              className="@[18rem]/queue-neighbor:size-14 size-10 shrink-0 rounded-md"
            />
            <div className="min-w-0 flex-1">
              <h3 className="break-words font-semibold @[18rem]/queue-neighbor:text-lg text-base leading-snug">
                {product?.title ?? neighbor.title}
              </h3>
              {product && (
                <p className="mt-1 text-muted-foreground text-sm">
                  {product.variants.length} ຕົວເລືອກ
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="py-5 text-muted-foreground text-sm">
            {direction === "previous" ? "ບໍ່ມີສິນຄ້າກ່ອນໜ້າ" : "ໝົດຄິວແລ້ວ"}
          </p>
        )}
      </CardHeader>
      {neighbor && (
        <CardContent className="px-3">
          {product ? (
            <ul
              aria-label={`${label} · ${product.title}`}
              tabIndex={product.variants.length > 3 ? 0 : undefined}
              className="max-h-36 space-y-2 overflow-y-auto overscroll-contain rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {product.variants.map((variant) => (
                <li
                  key={variant.sessionProductId}
                  className="flex min-w-0 @[12rem]/queue-neighbor:flex-row flex-col @[12rem]/queue-neighbor:items-center items-stretch justify-between gap-x-2 gap-y-1 rounded-md bg-muted/40 px-2 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="break-all font-bold font-mono text-base">
                      {variant.cfCode}
                    </p>
                    {variant.variantLabel && (
                      <p className="break-words text-muted-foreground text-sm">
                        {variant.variantLabel}
                      </p>
                    )}
                  </div>
                  <p className="text-right text-sm">
                    <span className="block text-muted-foreground">ຄົງເຫຼືອ</span>
                    <span className="font-semibold text-lg tabular-nums">
                      {variant.sessionRemaining ?? "ບໍ່ຈຳກັດ"}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">ບໍ່ພົບຂໍ້ມູນຕົວເລືອກ</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
